import { clsx } from 'clsx';
import { get } from 'lodash';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const VerificationStatus = {
  NeedsReview: 'NeedsReview',
  Auto: 'Auto',
  Manual: 'Manual',
};

export const UserRole = {
  User: 'User',
  Underwriter: 'Underwriter',
  Admin: 'Admin',
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  // Split the date string and reconstruct it to avoid timezone issues
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return dateString;
  return `${month}/${day}/${year}`;
};

export const needsReview = async (transaction, confidenceThreshold) => {
  // If the backend has already determined the status, use that
  if (transaction.verificationStatus !== undefined) {
    // Changed condition
    return transaction.verificationStatus === VerificationStatus.NeedsReview;
  }

  // Check for missing date
  if (!transaction.date || transaction.date.trim() === '') {
    console.log('Transaction needs review - Empty date');
    return true;
  }

  // Check for low date confidence
  if (
    typeof transaction.dateConfidence === 'number'
    && transaction.dateConfidence < confidenceThreshold
  ) {
    console.log(
      'Transaction needs review - Low date confidence:',
      transaction.dateConfidence
    );
    return true;
  }

  // Check transaction type and corresponding amounts
  switch (transaction.type) {
    case 'Deposit':
      if (
        transaction.depositAmount === null
        || typeof transaction.depositAmountConfidence !== 'number'
        || transaction.depositAmountConfidence < confidenceThreshold
      ) {
        console.log('Transaction needs review - Invalid deposit amount or confidence');
        return true;
      }
      break;
    case 'Withdrawal':
      if (
        transaction.withdrawalAmount === null
        || typeof transaction.withdrawalAmountConfidence !== 'number'
        || transaction.withdrawalAmountConfidence < confidenceThreshold
      ) {
        console.log('Transaction needs review - Invalid withdrawal amount or confidence');
        return true;
      }
      break;
    case 'Unknown':
      console.log('Transaction needs review - Unknown type');
      return true;
    default:
      console.log('Transaction needs review - Invalid type');
  }

  // Check overall amount confidence
  if (
    typeof transaction.amountConfidence === 'number'
    && transaction.amountConfidence < confidenceThreshold
  ) {
    console.log(
      'Transaction needs review - Low amount confidence:',
      transaction.amountConfidence
    );
    return true;
  }

  return false;
};

export const getTransactionCounts = async (accounts) => {
  if (!accounts) {
    return { needsReview: 0, verified: 0, manuallyVerified: 0 };
  }

  return accounts.reduce(async (accPromise, account) => {
    const acc = await accPromise;

    // Process each transaction in the account
    // eslint-disable-next-line
    for (const transaction of account.transactions) {
      if (transaction.verificationStatus === VerificationStatus.Manual) {
        acc.manuallyVerified++;
      } else if (transaction.verificationStatus === VerificationStatus.Auto) {
        acc.verified++;
      } else if (
        transaction.verificationStatus === VerificationStatus.NeedsReview
      ) {
        acc.needsReview++;
      } else {
        // Fallback to checking confidence scores if status not set
        // eslint-disable-next-line
        const isNeedsReview = await needsReview(transaction);
        if (isNeedsReview) {
          acc.needsReview++;
        } else {
          acc.verified++;
        }
      }
    }
    return acc;
  }, Promise.resolve({ needsReview: 0, verified: 0, manuallyVerified: 0 }));
};

export const isValidGuid = (guid) => {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return guidRegex.test(guid);
};

export const getErrorMessage = (error, msgFallback) => {
  const statusCode = get(error, 'status');
  let errorMsg = get(error, 'message') || msgFallback;
  if (statusCode === 423 || statusCode === 409) {
    errorMsg = 'This information has been updated while you were working on it. Please refresh and try again.';
  }
  return errorMsg;
};
